'use client';

import { ReactNode, useCallback } from 'react';
import {
  GamificationProvider,
  configureGamificationApi,
} from '@stadionhq-v3/gamification-react';
import { signIn, useSession } from 'next-auth/react';
// Side-effect: installs the axios interceptor that rewrites the SDK's
// hardcoded default host onto our same-origin `/api/gamification` proxy.
// Imported here (before the GamificationProvider import is used) so it
// registers as the provider chunk loads, ahead of the first SDK fetch.
import './setupAxios';
// Scottish FA gamification theme. `gamification-theme.css` is the SDK's
// `.gam__*-theme` defaults (pre-extracted, since the barrel is tree-shaken by
// `optimizePackageImports`). `scottishfa-gamification-overrides.css` layers SFA
// brand values on top, mapping `@stadionhq-v3/scottishfa-web-tokens` onto the
// gamification CSS custom properties.
import '@styles/vendor/gamification-theme.css';
import '@styles/vendor/scottishfa-gamification-overrides.css';

// Point the SDK's axios baseURL at our same-origin `/api/gamification/*`
// rewrite (next.config.mjs) synchronously at module load — before any
// provider's useEffect runs — so the SDK never tries the upstream host
// directly (which the browser can't reach cross-origin / CORS).
configureGamificationApi('/api/gamification');

export default function GamificationProviderWrapper({
  children,
}: {
  children: ReactNode;
}) {
  const { data: session, status } = useSession();
  const accessToken = session?.access_token;

  const handleSignIn = useCallback(() => {
    signIn('default', { callbackUrl: window.location.href });
  }, []);

  const handleRegister = useCallback(() => {
    signIn('register', { callbackUrl: window.location.href });
  }, []);

  return (
    <GamificationProvider
      apiBaseUrl="/api/gamification"
      authToken={accessToken}
      isAuthenticated={status === 'authenticated'}
      onSignIn={handleSignIn}
      onRegister={handleRegister}
    >
      {children}
    </GamificationProvider>
  );
}
